import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import Nav from "@/components/Nav";
import { Package, ArrowRight, Cube, StackSimple, Shapes, Sparkle } from "@phosphor-icons/react";

const CATEGORY_ORDER = ["Cartons", "Corrugated", "Specialty"];

// Tiny inline SVG "thumbnail" of each template — pulls the actual dieline SVG from /preview
function TemplateThumb({ slug, defaults }) {
  const [svg, setSvg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.post(`/dielines/${slug}/preview`, {
      length: defaults.L, width: defaults.W, height: defaults.H,
    }).then(({ data }) => { if (!cancelled) setSvg(data.svg); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug, defaults.L, defaults.W, defaults.H]);

  if (!svg) return (
    <div className="w-full h-40 bg-[var(--pf-surface-2)] rounded-md animate-pulse" />
  );
  return (
    <div
      className="w-full h-40 flex items-center justify-center bg-white rounded-md overflow-hidden"
      dangerouslySetInnerHTML={{ __html: svg.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[^"]*"/, 'height="100%"') }}
    />
  );
}

export default function DielineGallery() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Free Die-Cut Templates | Talaio/AI";
    api.get("/dielines/templates").then(({ data }) => {
      setTemplates(data.templates);
    }).finally(() => setLoading(false));
  }, []);

  const grouped = CATEGORY_ORDER.map(cat => ({
    cat, items: templates.filter(t => t.category === cat),
  })).filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pt-16 pb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-4">
          <Sparkle size={14} weight="fill" /> Now Free — no signup, no watermark
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4">
          Die-Cut Templates.<br/>
          <span className="text-[var(--pf-brand)]">Made to your size.</span>
        </h1>
        <p className="text-base md:text-lg text-[var(--pf-text-2)] max-w-2xl mb-8">
          Set length, width and height. We generate a production-ready, error-free
          dieline with cut and crease lines in SVG, PDF and DXF — plus a live 3D fold preview.
          No paid API. No account. No watermark.
        </p>
        <div className="flex flex-wrap gap-6 text-sm text-[var(--pf-text-2)]">
          <div className="flex items-center gap-2"><Cube size={18} weight="duotone"/> 3D fold preview</div>
          <div className="flex items-center gap-2"><StackSimple size={18} weight="duotone"/> SVG · PDF · DXF exports</div>
          <div className="flex items-center gap-2"><Shapes size={18} weight="duotone"/> {templates.length} template families</div>
          <div className="flex items-center gap-2"><Package size={18} weight="duotone"/> Logo overlay on preview</div>
        </div>
      </section>

      {/* Template grid, grouped by category */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-24">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border border-[var(--pf-border)] rounded-lg p-4 bg-white h-72 animate-pulse"/>
            ))}
          </div>
        )}
        {grouped.map(({ cat, items }) => (
          <div key={cat} className="mb-16">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight">{cat}</h2>
              <span className="text-xs pf-label">{items.length} template{items.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map(t => (
                <Link
                  key={t.id}
                  to={`/dielines/${t.slug}`}
                  data-testid={`dieline-card-${t.slug}`}
                  className="group border border-[var(--pf-border)] rounded-lg overflow-hidden bg-white hover:border-[var(--pf-brand)] hover:shadow-lg transition-all"
                >
                  <div className="p-4 border-b border-[var(--pf-border)] bg-[var(--pf-surface-2)]/40">
                    <TemplateThumb slug={t.slug} defaults={t.defaults}/>
                  </div>
                  <div className="p-5">
                    <div className="text-xs pf-label mb-2">{t.category}</div>
                    <h3 className="font-black text-lg mb-2 leading-tight">{t.name}</h3>
                    <p className="text-sm text-[var(--pf-text-2)] leading-relaxed mb-4 line-clamp-3">
                      {t.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {t.use_cases.slice(0, 3).map(u => (
                        <span key={u} className="px-2 py-0.5 rounded-full bg-[var(--pf-surface-2)] text-[10px] font-semibold uppercase tracking-wider text-[var(--pf-text-2)]">
                          {u}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold text-[var(--pf-brand)] group-hover:gap-3 transition-all">
                      <span>Customize & download</span>
                      <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform"/>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* Footer CTA */}
        <div className="mt-16 border border-[var(--pf-border)] rounded-lg p-8 md:p-12 bg-gradient-to-br from-[var(--pf-brand)]/10 to-transparent">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-3">
            Need a custom shape not listed here?
          </h2>
          <p className="text-[var(--pf-text-2)] mb-6 max-w-2xl">
            Send us the sketch or reference image and our design team will hand-cut a bespoke dieline within 24 hours.
            Included free for Startup Kit customers.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/startup-kit" data-testid="dieline-cta-kit" className="pf-btn-primary">
              Get the Startup Kit
            </Link>
            <Link to="/logo-studio" data-testid="dieline-cta-logo" className="pf-btn-secondary">
              Free logo first →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
